/* eslint-disable no-nested-ternary, react/jsx-one-expression-per-line */
import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import {
  Switch, Tooltip,
  IconButton, Snackbar, Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import DeleteIcon from '@mui/icons-material/Delete';
import BuildCircleOutlinedIcon from '@mui/icons-material/BuildCircleOutlined';
import SyncIcon from '@mui/icons-material/Sync';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { Link } from 'react-router-dom';
import ClipLoader from 'react-spinners/ClipLoader';
import {
  DataGridPro,
  GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
} from '@mui/x-data-grid-pro';
import { TrackstarConnectButton } from '@trackstar/react-trackstar-link';
import { useFetch } from './api';
import CustomDetailPanelToggle from './GridToggle';
import DeleteObjectModal from './DeleteObjectModal';
import CreateOnDemandSyncModal from './CreateOnDemandSyncModal';
import CopyButton from './CopyButton';
import EditableCustomerId from './EditableCustomerId';

// refined DataGrid styling for drawer
const drawerDataGridSx = {
  border: '1px solid #E7E5E4',
  backgroundColor: '#FFFFFF',
  fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
  '--DataGrid-rowBorderColor': '#F5F5F4',
  '& .MuiDataGrid-columnHeader, & .MuiDataGrid-scrollbarFiller, & .MuiDataGrid-filler': {
    backgroundColor: '#FAFAF9 !important',
  },
  '& .MuiDataGrid-columnHeaders': {
    borderBottom: '1px solid #E7E5E4',
    borderTopLeftRadius: '10px',
    borderTopRightRadius: '10px',
    fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
    fontSize: '0.8125rem',
    fontWeight: 500,
    color: '#78716C',
    minHeight: '36px !important',
    maxHeight: '36px !important',
  },
  '& .MuiDataGrid-columnHeader': {
    padding: '0 12px',
  },
  '& .MuiDataGrid-columnSeparator': {
    display: 'none',
  },
  '& .MuiDataGrid-cell': {
    fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
    fontSize: '0.8125rem',
    color: '#44403C',
    padding: '4px 12px',
  },
  '& .MuiDataGrid-detailPanel': {
    borderBottom: '1px solid #E7E5E4',
  },
  borderRadius: '10px',
  overflow: 'hidden',
};

function ConnectionDetailDrawer({
  showConnectionDetail, connectionDetail, fetchConnectionData,
  closeDrawer, onReinstallSuccess,
}) {
  const trackstar = useFetch();
  const [errorRows, setErrorRows] = useState([]);
  const [scheduleRows, setScheduleRows] = useState([]);
  const [scheduleRowsLoading, setScheduleRowsLoading] = useState(true);
  const [syncToast, setSyncToast] = useState(null);
  // each row's last crawl end when "Sync now" was clicked; the row stays syncing until that
  // changes, even if the api reports an error status in the meantime
  const [crawlEndAtSyncClick, setCrawlEndAtSyncClick] = useState({});
  const [accessToken, setAccessToken] = useState('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeletingRow, setIsDeletingRow] = useState(false);
  const [logo, setLogo] = useState('ts_logo_2024.png');
  const [copied, setCopied] = useState(false);
  const [safeMe, setSafeMe] = useState(null);
  const [safeMeLoading, setSafeMeLoading] = useState(false);
  const [activeConnectionTab, setActiveConnectionTab] = useState('schedules');
  const [onDemandSyncs, setOnDemandSyncs] = useState([]);
  const [availableFunctions, setAvailableFunctions] = useState([]);
  const [onDemandSyncsLoading, setOnDemandSyncsLoading] = useState(false);
  const [createSyncModalOpen, setCreateSyncModalOpen] = useState(false);
  const [createSyncLoading, setCreateSyncLoading] = useState(false);
  const [createSyncError, setCreateSyncError] = useState(null);
  const [cancellingSyncId, setCancellingSyncId] = useState(null);
  const [onDemandSyncsError, setOnDemandSyncsError] = useState(false);

  const getConnectionSchedules = (showLoading = true) => {
    setScheduleRowsLoading(showLoading);
    trackstar(`internal/schedules/${connectionDetail.connection_id}`, 'GET').then((response) => {
      const rows = Array.isArray(response?.data) ? response.data : [];
      const newScheduleRows = rows.map((schedule) => (
        { id: schedule.resource_name, ...schedule }));
      setScheduleRows(newScheduleRows);
      setScheduleRowsLoading(false);
    }).catch(() => {
      // a failed load must clear loading so the toggle doesn't stay disabled
      setScheduleRowsLoading(false);
    });
  };

  const getAccessToken = () => {
    trackstar(`internal/access-token?connection_id=${connectionDetail.connection_id}&integration_name=${connectionDetail.integration_name}`, 'GET').then((response) => {
      setAccessToken(response.access_token);
    });
  };

  const copyAccessToken = () => {
    navigator.clipboard.writeText(accessToken);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const toggleSchedule = (row) => {
    const newPaused = !row.paused;
    setScheduleRows((prev) => prev.map((r) => (
      r.id === row.id ? { ...r, paused: newPaused, sync_status: newPaused ? 'disabled' : 'enabled' } : r
    )));
    // resync rows + connection status on success OR failure (reverts the
    // optimistic flip if the request failed)
    const resync = () => {
      getConnectionSchedules();
      fetchConnectionData();
    };
    trackstar(
      `internal/schedules/${connectionDetail.connection_id}/${row.function_name}/toggle`,
      'PATCH',
      { paused: newPaused },
    ).then(resync, resync);
  };

  // connection reads as paused only when it has schedules and all of them are paused;
  // before rows load, fall back to the connection's paused flag to avoid a toggle flicker
  const allSchedulesPaused = scheduleRows.length > 0
    ? scheduleRows.every((r) => r.paused)
    : !!connectionDetail?.paused;

  // hide the bulk toggle for connections with no schedules (e.g. a fresh sandbox).
  // show it while schedules are still loading so it doesn't flicker in; only hide
  // once the fetch confirms there are none.
  const hasSchedules = scheduleRowsLoading || scheduleRows.length > 0;

  const toggleAllSchedules = () => {
    const newPaused = !allSchedulesPaused;
    setScheduleRows((prev) => prev.map((r) => (
      { ...r, paused: newPaused, sync_status: newPaused ? 'disabled' : 'enabled' }
    )));
    // resync rows + connection status on success OR failure (reverts the
    // optimistic flip across all rows if the request failed)
    const resync = () => {
      getConnectionSchedules();
      fetchConnectionData();
    };
    trackstar(
      `internal/schedules/${connectionDetail.connection_id}/toggle-all`,
      'PATCH',
      { paused: newPaused },
    ).then(resync, resync);
  };

  const isSyncing = (row) => {
    if (row.sync_status.includes('in_progress')) return true;
    if (!(row.id in crawlEndAtSyncClick)) return false;
    return row.last_crawl_end === crawlEndAtSyncClick[row.id];
  };

  const handleSyncNow = (row) => {
    setCrawlEndAtSyncClick((prev) => ({ ...prev, [row.id]: row.last_crawl_end }));
    trackstar(
      `internal/connections/sync/${connectionDetail.connection_id}`,
      'POST',
      { functions_to_sync: [row.function_name] },
    ).then((response) => {
      // useFetch resolves on 4xx/5xx, so a failure only shows up as an error body
      if (response?.error) throw new Error(response.error);
      setSyncToast({ severity: 'success', message: `Syncing ${row.resource_name}. Status updates here when it finishes.` });
    }).catch(() => {
      setCrawlEndAtSyncClick((prev) => {
        const next = { ...prev };
        delete next[row.id];
        return next;
      });
      setSyncToast({ severity: 'error', message: `Couldn't start a sync for ${row.resource_name}. Try again in a minute.` });
    }).finally(() => getConnectionSchedules(false));
  };

  // while anything is syncing, keep refreshing so the status changes without a reload
  const anySyncing = scheduleRows.some(isSyncing);
  const wasSyncing = useRef(false);
  useEffect(() => {
    if (!showConnectionDetail || !anySyncing) return undefined;
    const interval = setInterval(() => getConnectionSchedules(false), 10000);
    return () => clearInterval(interval);
  }, [showConnectionDetail, anySyncing, connectionDetail?.connection_id]);

  // a finished sync can clear or raise errors, so refresh the connection too
  useEffect(() => {
    if (wasSyncing.current && !anySyncing) {
      fetchConnectionData();
    }
    wasSyncing.current = anySyncing;
  }, [anySyncing]);

  const handleEditCustomerId = (customerId) => trackstar(
    `internal/connections/${connectionDetail.connection_id}`,
    'PATCH',
    { customer_id: customerId },
  ).then((response) => {
    // useFetch resolves on 4xx/5xx, so a rejected save only surfaces as an
    // error body; rethrow it so the editor can report the failure
    if (response?.error) throw new Error(response.error);
    // returned so the editor closes on fresh rows rather than flashing the old value
    return fetchConnectionData();
  });

  const handleOpenDeleteModal = () => {
    setDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = (cancelled) => {
    if (!cancelled) {
      fetchConnectionData();
    }
    setDeleteModalOpen(false);
  };

  const handleDeleteConnection = () => {
    setIsDeletingRow(true);
    trackstar(`internal/connections/${connectionDetail.connection_id}`, 'DELETE').then(() => {
      handleCloseDeleteModal(false);
      setIsDeletingRow(false);
      closeDrawer();
    });
  };

  const getOnDemandSyncs = () => {
    setOnDemandSyncsLoading(true);
    setOnDemandSyncsError(false);
    trackstar(`internal/connections/${connectionDetail.connection_id}/on-demand-syncs`, 'GET')
      .then((response) => {
        if (!response || response.error || !Array.isArray(response.data)) {
          setOnDemandSyncsError(true);
          setOnDemandSyncs([]);
          setAvailableFunctions([]);
          return;
        }
        setOnDemandSyncs(response.data);
        setAvailableFunctions(response.available_functions || []);
      })
      .catch(() => {
        setOnDemandSyncsError(true);
        setOnDemandSyncs([]);
        setAvailableFunctions([]);
      })
      .finally(() => setOnDemandSyncsLoading(false));
  };

  const handleCreateOnDemandSync = (payload) => {
    setCreateSyncLoading(true);
    setCreateSyncError(null);
    trackstar(
      `internal/connections/${connectionDetail.connection_id}/on-demand-syncs`,
      'POST',
      payload,
    )
      .then((response) => {
        if (response && response.error) {
          setCreateSyncError(response.error);
        } else {
          setCreateSyncModalOpen(false);
          getOnDemandSyncs();
        }
      })
      .catch((err) => {
        setCreateSyncError(err?.message || 'Failed to create sync job.');
      })
      .finally(() => setCreateSyncLoading(false));
  };

  const handleCancelOnDemandSync = (syncId) => {
    setCancellingSyncId(syncId);
    trackstar(
      `internal/connections/${connectionDetail.connection_id}/on-demand-syncs/${syncId}/cancel`,
      'POST',
    )
      .then(() => {
        getOnDemandSyncs();
      })
      .finally(() => setCancellingSyncId(null));
  };

  const getSafeMe = () => {
    setSafeMe(null);
    setSafeMeLoading(true);
    trackstar(`internal/connections/${connectionDetail.connection_id}/safe-me`, 'GET')
      .then((response) => {
        setSafeMe(response?.data || null);
      })
      .catch(() => setSafeMe(null))
      .finally(() => setSafeMeLoading(false));
  };

  useEffect(() => {
    if (showConnectionDetail && connectionDetail) {
      // clear the previous connection's rows so the toggle falls back to this
      // connection's paused flag instead of flashing stale/empty state
      setScheduleRows([]);
      setCrawlEndAtSyncClick({});
      getConnectionSchedules();
      getOnDemandSyncs();
      setActiveConnectionTab('schedules');
      getAccessToken();
      getSafeMe();
      if (connectionDetail.integration_name === 'sandbox') {
        setLogo('ts_logo_2024.png');
      } else {
        setLogo(`https://trackstarlogosbucket.s3.amazonaws.com/${connectionDetail.integration_name}.png`);
      }
    }
  }, [showConnectionDetail, connectionDetail?.connection_id]);

  useEffect(() => {
    if (connectionDetail) {
      setErrorRows(connectionDetail.errors.map((error, index) => ({ id: index, ...error })));
    }
  }, [connectionDetail?.errors]);

  // Status badge styles
  const getStatusStyles = (status) => {
    const styles = {
      OK: 'badge-success',
      Warning: 'badge-warning',
      Errors: 'badge-error',
      Disabled: 'badge-neutral',
    };
    return styles[status] || 'badge-neutral';
  };

  // Status dot styles
  const getStatusDotStyles = (status) => {
    const styles = {
      OK: 'status-dot-success',
      Warning: 'status-dot-warning',
      Errors: 'status-dot-error',
      Disabled: 'status-dot-neutral',
    };
    return styles[status] || 'status-dot-neutral';
  };

  // Sync status badge
  const getSyncStatusBadge = (status) => {
    if (status === 'disabled') {
      return 'badge-neutral';
    }
    if (status === 'error') {
      return 'badge-error';
    }
    if (status === 'stuck') {
      return 'badge-warning';
    }
    return 'badge-success';
  };

  const capitalizeFirst = (str) => {
    // Shorten first_sync_in_progress and sync_in_progress to fit in badge
    let shortenedStr = str;
    if (str === 'first_sync_in_progress' || str === 'sync_in_progress') {
      shortenedStr = 'syncing';
    }
    const formatted = shortenedStr.replace(/_/g, ' ');
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  };

  const formatTimestamp = (value) => {
    if (!value) return '—';
    return new Date(value).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
  };

  const getOnDemandStatusBadge = (status) => {
    if (status === 'completed') return 'badge-success';
    if (status === 'open') return 'badge-warning';
    if (status === 'stuck') return 'badge-warning';
    return 'badge-neutral';
  };

  const formatRequestedWindow = (start, end) => {
    if (!start || !end) return '—';
    return `${formatTimestamp(start)} → ${formatTimestamp(end)}`;
  };

  const formatFrequency = (seconds) => {
    if (!seconds) return '—';
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.round(seconds / 60);
    if (mins < 60) return `${mins} min`;
    const hrs = Math.round(mins / 60);
    return hrs === 1 ? '1 hour' : `${hrs} hours`;
  };

  // strip spaces, hyphens, underscores for matching resource names to endpoint segments
  const normalize = (s) => s?.toLowerCase().replace(/[\s_-]/g, '') || '';

  const hasMatchedErrors = (resourceName) => {
    const norm = normalize(resourceName);
    return errorRows.some((error) => error.affected_endpoints?.some(
      (ep) => normalize(ep.endpoint.split('/')[2]) === norm,
    ));
  };

  const getDetailPanelContent = useCallback((params) => {
    const { row } = params;
    const resourceNorm = normalize(row.resource_name);
    const matchedErrors = errorRows.filter((error) => error.affected_endpoints?.some(
      (ep) => normalize(ep.endpoint.split('/')[2]) === resourceNorm,
    ));

    return (
      <div className="px-6 py-2 flex gap-3 items-stretch">
        <div className="flex-1 min-w-0 p-2.5 bg-surface-50 rounded-lg border border-surface-200">
          <p className="text-[11px] font-medium text-surface-400 uppercase tracking-wider mb-1.5">Sync Details</p>
          <div className="grid grid-cols-[auto_auto] gap-x-4 gap-y-1 text-xs">
            <div className="flex items-center gap-1">
              <span className="text-surface-400">Last Sync Start</span>
              <Tooltip title="When the last sync job started running" arrow placement="top">
                <img src="infov2.png" alt="info" className="h-3 w-3 opacity-40 cursor-pointer" />
              </Tooltip>
            </div>
            <span className="text-surface-600 font-medium">{formatTimestamp(row.last_crawl_start)}</span>
            <div className="flex items-center gap-1">
              <span className="text-surface-400">Last Sync End</span>
              <Tooltip title="When the last sync job finished" arrow placement="top">
                <img src="infov2.png" alt="info" className="h-3 w-3 opacity-40 cursor-pointer" />
              </Tooltip>
            </div>
            <span className="text-surface-600 font-medium">{formatTimestamp(row.last_crawl_end)}</span>
            <div className="flex items-center gap-1">
              <span className="text-surface-400">Latest Data</span>
              <Tooltip title="The most recent data timestamp synced for this resource" arrow placement="top">
                <img src="infov2.png" alt="info" className="h-3 w-3 opacity-40 cursor-pointer" />
              </Tooltip>
            </div>
            <span className="text-surface-600 font-medium">{formatTimestamp(row.latest_data)}</span>
          </div>
        </div>
        <div className="flex-1 min-w-0 p-2.5 bg-surface-50 rounded-lg border border-surface-200 flex flex-col">
          <p className="text-[11px] font-medium text-surface-400 uppercase tracking-wider mb-1.5">Credential Errors</p>
          {matchedErrors.length > 0 ? (
            <div className="flex flex-col gap-1.5 flex-1">
              {matchedErrors.map((error) => {
                const matchingEp = error.affected_endpoints.find(
                  (ep) => normalize(ep.endpoint.split('/')[2]) === resourceNorm,
                );
                return (
                  <div
                    key={error.id}
                    className={`pl-3 py-1.5 border-l-2 ${
                      matchingEp.preventing_sync ? 'border-error-dark' : 'border-warning'
                    }`}
                  >
                    <p className="text-sm text-surface-700">{error.error_message}</p>
                    <p className="text-xs text-surface-400 mt-0.5">
                      {matchingEp.preventing_sync ? 'Blocking sync' : 'Non-blocking'}
                      {' · '}
                      First seen {formatTimestamp(error.first_seen)}
                    </p>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex-1 flex items-center">
              <div className="pl-3 py-1.5 border-l-2 border-success-dark">
                <p className="text-sm text-surface-700">No credential or permission errors</p>
                <p className="text-xs text-surface-400 mt-0.5">Sync running normally</p>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }, [errorRows]);

  const getDetailPanelHeight = useCallback(() => 'auto', []);

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Header with blur effect */}
      <div className="sticky top-0 z-10 bg-surface-0/90 backdrop-blur-md border-b border-surface-200 px-6 py-3">
        <div className="flex justify-between items-center">
          <span className="text-sm font-medium text-surface-500">Connection Details</span>
          <IconButton
            onClick={closeDrawer}
            size="small"
            className="hover:bg-surface-100"
            sx={{ padding: '4px' }}
          >
            <CloseIcon sx={{ fontSize: '18px', color: '#78716C' }} />
          </IconButton>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-6 py-5" style={{ scrollbarGutter: 'stable' }}>
        {/* Connection info header */}
        <div className="flex justify-between items-center gap-4 mb-5">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="w-10 h-10 shrink-0 rounded-md bg-surface-100 flex items-center justify-center border border-surface-200">
              <img src={logo} alt={`${connectionDetail?.integration_name} logo`} className="h-6 w-6" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <p className="text-sm font-semibold text-surface-800 capitalize">{connectionDetail?.integration_name}</p>
                <span className="text-xs text-surface-400">
                  {{
                    wms: 'WMS', cart: 'Cart', carrier: 'Carrier', freight: 'Freight', erp: 'ERP',
                  }[connectionDetail?.integration_type] || connectionDetail?.integration_type}
                </span>
                <span className={`badge ${getStatusStyles(connectionDetail?.status)} w-[90px] shrink-0 justify-center`}>
                  <span className={`status-dot ${getStatusDotStyles(connectionDetail?.status)}`} />
                  {connectionDetail?.status === 'Warning' ? 'Warn' : connectionDetail?.status}
                </span>
              </div>
              {/* kept to one row so the block stays level with the actions box */}
              <div className="flex items-center gap-1 min-w-0 mt-0.5">
                <p className="font-mono text-xs text-surface-500 truncate">
                  {connectionDetail?.connection_id}
                </p>
                <CopyButton
                  value={connectionDetail?.connection_id}
                  label="Copy Connection ID"
                  iconSize="12px"
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1 p-1.5 bg-surface-0 rounded-lg border border-surface-200 shadow-xs shrink-0">
            <Tooltip title={connectionDetail?.webhooks_disabled ? 'Enable Webhooks' : 'Disable Webhooks'} arrow>
              <div className="flex items-center px-2 border-r border-surface-200">
                <span className="text-xs font-medium text-surface-500 mr-1">Webhooks</span>
                <Switch
                  checked={!connectionDetail?.webhooks_disabled}
                  onChange={() => {
                    trackstar(`internal/connections/${connectionDetail.connection_id}`, 'PATCH', { webhooks_disabled: !connectionDetail.webhooks_disabled }).then(() => {
                      fetchConnectionData();
                    });
                  }}
                  size="small"
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#1C1917',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#1C1917',
                    },
                  }}
                />
              </div>
            </Tooltip>
            <Tooltip title="View API Logs" arrow>
              <IconButton
                component={Link}
                to={`/api-log?connection=${connectionDetail?.connection_id}`}
                size="small"
                className="text-surface-500 hover:text-surface-700 hover:bg-surface-100"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </IconButton>
            </Tooltip>
            <Tooltip title="View Webhook Logs" arrow>
              <IconButton
                component={Link}
                to={`/webhooks?tags=${connectionDetail?.connection_id}`}
                size="small"
                className="text-surface-500 hover:text-surface-700 hover:bg-surface-100"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
                </svg>
              </IconButton>
            </Tooltip>
            <Tooltip title="Open in Data Explorer" arrow>
              <IconButton
                component={Link}
                to={`/data-explorer?mode=production&connection=${connectionDetail?.connection_id}`}
                size="small"
                className="text-surface-500 hover:text-surface-700 hover:bg-surface-100"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
                </svg>
              </IconButton>
            </Tooltip>
            <Tooltip title={copied ? 'Copied!' : 'Copy Access Token'} arrow>
              <IconButton onClick={copyAccessToken} size="small" className="text-surface-500 hover:text-surface-700 hover:bg-surface-100">
                <VpnKeyIcon sx={{ fontSize: '16px' }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Reinstall Connection" arrow>
              <span>
                <TrackstarConnectButton
                  key={`reinstall-${connectionDetail?.connection_id}`}
                  buttonId={`reinstall-${connectionDetail?.connection_id}`}
                  integrationType={connectionDetail?.integration_type}
                  getLinkToken={async () => {
                    const response = await trackstar('internal/link/token', 'POST', {
                      connection_id: connectionDetail?.connection_id,
                    });
                    return response.link_token;
                  }}
                  onSuccess={async (authCode) => {
                    const response = await trackstar('internal/link/exchange', 'POST', { auth_code: authCode });
                    onReinstallSuccess(response);
                  }}
                  onClose={() => {}}
                  onLoad={() => {}}
                  style={{
                    border: '0px',
                    padding: '2',
                    display: 'flex',
                    background: 'transparent',
                  }}
                >
                  <IconButton size="small" className="text-surface-500 hover:text-surface-700 hover:bg-surface-100 transition-colors">
                    <BuildCircleOutlinedIcon fontSize="small" />
                  </IconButton>
                </TrackstarConnectButton>
              </span>
            </Tooltip>
            {/* deleting a sandbox resets the home dashboard to onboarding */}
            {connectionDetail?.integration_name !== 'sandbox' && (
              <Tooltip title="Delete Connection" arrow>
                <IconButton onClick={handleOpenDeleteModal} size="small" className="text-surface-500 hover:text-error-dark hover:bg-error-light">
                  <DeleteIcon sx={{ fontSize: '16px' }} />
                </IconButton>
              </Tooltip>
            )}
          </div>
        </div>

        {/* Connection stats */}
        <div className="grid grid-cols-4 gap-3 mb-4">
          {/* the open editor is wider than this card; relative+z lets it spill over
              the next card instead of being painted under it */}
          <div className="relative z-10 px-3 py-2 bg-surface-50 rounded-lg border border-surface-200 min-w-0">
            <p className="text-[10px] font-medium text-surface-400 uppercase tracking-wider">Customer ID</p>
            <EditableCustomerId
              value={connectionDetail?.customer_id}
              onSave={handleEditCustomerId}
              placeholder="Not set"
              valueClassName="text-sm font-medium text-surface-700"
              inputClassName="w-32"
              iconSize="14px"
            >
              {/* passed as a child so it drops away while the editor is open */}
              <CopyButton
                value={connectionDetail?.customer_id}
                label="Copy Customer ID"
                iconSize="12px"
              />
            </EditableCustomerId>
          </div>
          <div className="px-3 py-2 bg-surface-50 rounded-lg border border-surface-200">
            <p className="text-[10px] font-medium text-surface-400 uppercase tracking-wider">Created</p>
            <p className="text-sm font-medium text-surface-700">
              {new Date(connectionDetail?.created_at).toLocaleString([], {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}
            </p>
          </div>
          <div className="px-3 py-2 bg-surface-50 rounded-lg border border-surface-200">
            <p className="text-[10px] font-medium text-surface-400 uppercase tracking-wider">API Calls</p>
            <p className="text-sm font-medium text-surface-700">{(connectionDetail?.times_used || 0).toLocaleString()}</p>
          </div>
          <div className="px-3 py-2 bg-surface-50 rounded-lg border border-surface-200">
            <p className="text-[10px] font-medium text-surface-400 uppercase tracking-wider">Last Used</p>
            <p className="text-sm font-medium text-surface-700">
              {connectionDetail?.last_used
                ? new Date(connectionDetail.last_used).toLocaleString([], {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })
                : 'Never'}
            </p>
          </div>
        </div>

        {/* Account (safe_me) */}
        <div className="mb-5 bg-surface-50 rounded-lg border border-surface-200 overflow-hidden">
          <div className="px-3 py-2 border-b border-surface-200 bg-surface-0/50">
            <p className="text-[10px] font-medium text-surface-400 uppercase tracking-wider">Integration Account Info</p>
          </div>
          <div className="px-3 py-2">
            {safeMeLoading && (
              <ClipLoader size={14} color="#78716C" />
            )}
            {!safeMeLoading && safeMe && typeof safeMe === 'object' && Object.keys(safeMe).length > 0 && (
              <div className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-1.5 text-xs items-baseline">
                {Object.entries(safeMe).map(([k, v]) => (
                  <React.Fragment key={k}>
                    <span className="text-surface-500">{k}</span>
                    <span className="text-surface-800 font-mono break-all">
                      {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                    </span>
                  </React.Fragment>
                ))}
              </div>
            )}
            {!safeMeLoading && safeMe && typeof safeMe !== 'object' && (
              <span className="text-xs font-mono text-surface-700">{String(safeMe)}</span>
            )}
            {!safeMeLoading && !safeMe && (
              <span className="text-xs text-surface-400">Not available</span>
            )}
          </div>
        </div>

        {/* schedules + on-demand only make sense when the connection has schedules */}
        {hasSchedules && (
        <>
          {/* Tab nav: Sync Schedules | On-Demand Syncs */}
          <div className="flex gap-0 border-b border-surface-200 mb-3">
            {[
              { key: 'schedules', label: 'Sync Schedules' },
              { key: 'onDemand', label: 'On-Demand Syncs' },
            ].map(({ key, label }) => {
              const active = activeConnectionTab === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setActiveConnectionTab(key)}
                  style={{ height: '36px' }}
                  className={`flex-1 flex items-center justify-center gap-1.5 px-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors -mb-px ${
                    active
                      ? 'border-surface-800 text-surface-800'
                      : 'border-transparent text-surface-500 hover:text-surface-700'
                  }`}
                >
                  <span>{label}</span>
                </button>
              );
            })}
          </div>

          {/* Sync Schedules tab */}
          {activeConnectionTab === 'schedules' && (
          <DataGridPro
            rows={scheduleRows}
            autoHeight
            rowHeight={36}
            columnHeaderHeight={36}
            loading={scheduleRowsLoading}
            disableColumnMenu
            getDetailPanelContent={getDetailPanelContent}
            getDetailPanelHeight={getDetailPanelHeight}
            columns={[
              {
                ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
                width: 20,
                renderCell: (params) => (
                  <CustomDetailPanelToggle value={params.value} />
                ),
              },
              {
                field: 'resource_name',
                headerName: 'Resource',
                display: 'flex',
                flex: 1.3,
                renderHeader: () => (
                  <div className="flex items-center gap-1.5">
                    <span>Resource</span>
                    <Tooltip title="The synced resource type" arrow>
                      <img src="infov2.png" alt="info" className="h-3.5 w-3.5 opacity-40" />
                    </Tooltip>
                  </div>
                ),
                renderCell: (params) => (
                  <span className="font-medium text-surface-700">{params.value}</span>
                ),
              },
              {
                field: 'crawl_frequency',
                headerName: 'Frequency',
                display: 'flex',
                width: 150,
                renderHeader: () => (
                  <div className="flex items-center gap-1.5">
                    <span>Frequency</span>
                    <Tooltip title="How often this resource syncs" arrow>
                      <img src="infov2.png" alt="info" className="h-3.5 w-3.5 opacity-40" />
                    </Tooltip>
                  </div>
                ),
                renderCell: (params) => {
                  let syncNowTip = `Fetch new and changed ${params.row.resource_name} now instead of waiting for the next scheduled sync. Picks up from Latest Data; it doesn't reload older history (use On-Demand Syncs for that).`;
                  if (params.row.paused) {
                    syncNowTip = 'Turn this schedule back on to sync it.';
                  } else if (isSyncing(params.row)) {
                    syncNowTip = 'A sync is already running.';
                  }
                  return (
                    <div className="flex items-center justify-between w-full gap-2">
                      <span className="text-surface-600 text-xs">{formatFrequency(params.value)}</span>
                      <Tooltip title={syncNowTip} arrow placement="top" slotProps={{ tooltip: { sx: { maxWidth: 280 } } }}>
                        <span>
                          <button
                            type="button"
                            onClick={() => handleSyncNow(params.row)}
                            disabled={params.row.paused || isSyncing(params.row)}
                            className="flex items-center gap-1 text-xs font-medium text-surface-600 hover:text-surface-900 disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            <SyncIcon sx={{ fontSize: '14px' }} />
                            Sync now
                          </button>
                        </span>
                      </Tooltip>
                    </div>
                  );
                },
              },
              {
                field: 'latest_data',
                headerName: 'Latest Data',
                display: 'flex',
                flex: 1,
                minWidth: 150,
                renderHeader: () => (
                  <div className="flex items-center gap-1.5">
                    <span>Latest Data</span>
                    <Tooltip title="The last time data was updated for this resource" arrow>
                      <img src="infov2.png" alt="info" className="h-3.5 w-3.5 opacity-40" />
                    </Tooltip>
                  </div>
                ),
                renderCell: (params) => {
                  if (!params.value) return <span className="text-surface-400">—</span>;
                  return <span className="text-surface-600 text-xs">{formatTimestamp(params.value)}</span>;
                },
              },
              {
                field: 'sync_status',
                headerName: 'Status',
                display: 'flex',
                width: 114,
                renderHeader: () => (
                  <div className="flex items-center gap-1.5">
                    <span>Status</span>
                    <Tooltip title="Sync status of the schedule" arrow>
                      <img src="infov2.png" alt="info" className="h-3.5 w-3.5 opacity-40" />
                    </Tooltip>
                  </div>
                ),
                renderCell: (params) => {
                  // the api reports error even on a paused schedule, so check paused first
                  let badge = getSyncStatusBadge(params.value);
                  let label = capitalizeFirst(params.value);
                  if (params.row.paused) {
                    badge = 'badge-neutral';
                    label = 'Paused';
                  } else if (isSyncing(params.row)) {
                    badge = 'badge-success';
                    label = 'Syncing';
                  } else if (params.value !== 'error' && hasMatchedErrors(params.row.resource_name)) {
                    badge = 'badge-warning';
                    label = 'Warn';
                  } else if (params.value === 'enabled') {
                    label = 'Healthy';
                  }
                  return (
                    <span className={`badge ${badge} w-[90px] shrink-0 justify-center`}>
                      {label}
                    </span>
                  );
                },
              },
              {
                field: 'controls',
                headerName: '',
                display: 'flex',
                width: 70,
                sortable: false,
                renderHeader: () => (
                  <Tooltip title={allSchedulesPaused ? 'Resume all schedules' : 'Pause all schedules'} arrow>
                    <span>
                      <Switch
                        checked={!allSchedulesPaused}
                        disabled={scheduleRowsLoading || scheduleRows.length === 0}
                        onChange={toggleAllSchedules}
                        size="small"
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': { color: '#1C1917' },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#1C1917' },
                          '& .MuiSwitch-switchBase.Mui-checked.Mui-disabled': { color: '#1C1917' },
                          '& .MuiSwitch-switchBase.Mui-checked.Mui-disabled + .MuiSwitch-track': { backgroundColor: '#1C1917' },
                        }}
                      />
                    </span>
                  </Tooltip>
                ),
                renderCell: (params) => (
                  <Tooltip title={params.row.paused ? 'Resume Schedule' : 'Pause Schedule'} arrow>
                    <Switch
                      checked={!params.row.paused}
                      onChange={() => toggleSchedule(params.row)}
                      size="small"
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#1C1917',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#1C1917',
                        },
                      }}
                    />
                  </Tooltip>
                ),
              },
            ]}
            sx={drawerDataGridSx}
            hideFooter
          />
          )}

          {/* On-Demand Syncs tab */}
          {activeConnectionTab === 'onDemand' && (
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs text-surface-500 flex-1">
                Load a specific data type over a custom time window. For more information, see our{' '}
                <a
                  href="https://docs.trackstarhq.com/how-to-guides/syncing-data#on-demand-syncs"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent-600 underline"
                >
                  documentation
                </a>.
              </p>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Tooltip title="Refresh" arrow>
                  <IconButton
                    onClick={getOnDemandSyncs}
                    size="small"
                    className="text-surface-500 hover:text-surface-700"
                    disabled={onDemandSyncsLoading}
                  >
                    <RefreshIcon sx={{ fontSize: '16px' }} />
                  </IconButton>
                </Tooltip>
                <Tooltip
                  title={availableFunctions.length === 0 ? 'No on-demand sync functions are enabled for this integration.' : ''}
                  arrow
                  placement="top"
                  slotProps={{ tooltip: { sx: { maxWidth: 360 } } }}
                >
                  <span>
                    <button
                      type="button"
                      onClick={() => {
                        setCreateSyncError(null);
                        setCreateSyncModalOpen(true);
                      }}
                      disabled={availableFunctions.length === 0}
                      className="btn btn-accent flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <AddIcon sx={{ fontSize: '14px' }} />
                      Create
                    </button>
                  </span>
                </Tooltip>
              </div>
            </div>
            <div className="border border-surface-200 rounded-lg bg-white overflow-hidden">
              {onDemandSyncsLoading && onDemandSyncs.length === 0 ? (
                <div className="flex items-center justify-center p-6">
                  <ClipLoader size={20} color="#D4532A" />
                </div>
              ) : onDemandSyncsError ? (
                <div className="px-4 py-6 text-center text-sm text-error-dark">
                  Unable to load on-demand syncs. Try refreshing.
                </div>
              ) : onDemandSyncs.length === 0 ? (
                <div className="px-4 py-6 text-center text-sm text-surface-400">
                  No on-demand syncs created yet.
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-surface-50 sticky top-0 z-10">
                    <tr className="text-left text-xs font-medium text-surface-500">
                      <th className="px-4 py-2">Function</th>
                      <th className="px-4 py-2">Status</th>
                      <th className="px-4 py-2">Requested Window</th>
                      <th className="px-2 py-2 w-8" />
                    </tr>
                  </thead>
                  <tbody>
                    {onDemandSyncs.map((job) => (
                      <tr key={job.id} className="border-t border-surface-100">
                        <td className="px-4 py-2 font-medium text-surface-700 font-mono text-xs">
                          {job.function_name}
                        </td>
                        <td className="px-4 py-2">
                          <span className={`badge ${getOnDemandStatusBadge(job.status)} w-[90px] shrink-0 justify-center`}>
                            {capitalizeFirst(job.status)}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-surface-600 text-xs">
                          {formatRequestedWindow(job.requested_start_time, job.requested_end_time)}
                        </td>
                        <td className="px-2 py-2">
                          {job.status === 'open' && (
                            <Tooltip title="Cancel sync job" arrow>
                              <IconButton
                                onClick={() => handleCancelOnDemandSync(job.id)}
                                size="small"
                                disabled={cancellingSyncId === job.id}
                                className="text-surface-500 hover:text-error-dark hover:bg-error-light"
                              >
                                <CancelOutlinedIcon sx={{ fontSize: '16px' }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
          )}
        </>
        )}

      </div>

      {/* Modals */}
      <Snackbar
        open={!!syncToast}
        autoHideDuration={5000}
        onClose={() => setSyncToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSyncToast(null)} severity={syncToast?.severity} variant="filled">
          {syncToast?.message}
        </Alert>
      </Snackbar>
      <DeleteObjectModal
        isOpen={deleteModalOpen}
        onClose={handleCloseDeleteModal}
        onDelete={handleDeleteConnection}
        isLoading={isDeletingRow}
        row={connectionDetail}
        objectType="Connection"
      />
      <CreateOnDemandSyncModal
        isOpen={createSyncModalOpen}
        onClose={() => setCreateSyncModalOpen(false)}
        onCreate={handleCreateOnDemandSync}
        isLoading={createSyncLoading}
        availableFunctions={availableFunctions}
        error={createSyncError}
      />
    </div>
  );
}

export default ConnectionDetailDrawer;
